import { Link } from "react-router-dom";
import { Heart, Plus, Star } from "lucide-react";
import clsx from "clsx";
import { Card } from "../primitives/Card";
import { PriceTag, getDiscountPercent } from "./PriceTag";

const LOW_STOCK_THRESHOLD = 5;

export interface ProductCardProps {
  slug: string;
  name: string;
  shortDescription?: string | null;
  imageUrl: string | null;
  basePrice: number;
  compareAtPrice?: number | null;
  soldCount?: number;
  // Total units left across all variants. Omit if unknown (e.g. a listing
  // that didn't fetch variants) — the badge only ever appears when this is
  // explicitly provided, never inferred.
  stock?: number;
  wishlisted?: boolean;
  onToggleWishlist?: () => void;
  // Only rendered when count > 0 — a product with no reviews shows no
  // rating row at all rather than a fake "0.0".
  rating?: { average: number; count: number };
  // Renders a quick-add button on the image; caller decides what "add"
  // means (e.g. the first variant) since ProductCard has no variant model.
  onQuickAdd?: () => void;
  className?: string;
}

export function ProductCard({
  slug,
  name,
  shortDescription,
  imageUrl,
  basePrice,
  compareAtPrice,
  soldCount,
  stock,
  wishlisted,
  onToggleWishlist,
  rating,
  onQuickAdd,
  className
}: ProductCardProps) {
  const discountPercent = getDiscountPercent(basePrice, compareAtPrice);
  const isOutOfStock = stock === 0;
  const isLowStock = typeof stock === "number" && stock > 0 && stock <= LOW_STOCK_THRESHOLD;

  return (
    <Card interactive tilt className={clsx("group overflow-hidden", className)}>
      <Link to={`/product/${slug}`} className="block">
        <div className="aspect-square bg-surface relative overflow-hidden">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={name}
              loading="lazy"
              className={clsx(
                "w-full h-full object-cover transition-transform duration-700 ease-physical group-hover:scale-105",
                isOutOfStock && "opacity-50 grayscale"
              )}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-ink-muted text-sm">No image</div>
          )}
          <div className="absolute top-3 left-3 flex flex-col items-start gap-1.5">
            {discountPercent !== null && (
              <span className="rounded-full bg-red-500 text-white text-xs font-medium px-2.5 py-1">
                {discountPercent}% OFF
              </span>
            )}
            {isOutOfStock ? (
              <span className="rounded-full bg-ink/80 text-canvas text-xs font-medium px-2.5 py-1">Out of stock</span>
            ) : (
              isLowStock && (
                <span className="rounded-full bg-amber-500 text-white text-xs font-medium px-2.5 py-1">
                  Only {stock} left
                </span>
              )
            )}
          </div>
          {onToggleWishlist && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                onToggleWishlist();
              }}
              aria-label="Toggle wishlist"
              className="absolute top-2 right-2 h-11 w-11 rounded-full glass flex items-center justify-center text-ink hover:scale-110 active:scale-95 transition-transform duration-300 ease-physical"
            >
              <Heart size={16} fill={wishlisted ? "currentColor" : "none"} />
            </button>
          )}
          {onQuickAdd && !isOutOfStock && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                onQuickAdd();
              }}
              aria-label="Quick add to cart"
              className="absolute bottom-2 right-2 h-11 w-11 rounded-full glass flex items-center justify-center text-ink opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 md:transition-all md:duration-300 md:ease-physical max-md:opacity-100 max-md:translate-y-0 hover:scale-110 active:scale-95"
            >
              <Plus size={16} />
            </button>
          )}
        </div>
        <div className="p-4">
          <p className="text-sm font-medium text-ink truncate">{name}</p>
          {shortDescription && (
            <p className="text-xs text-ink-muted truncate mt-0.5">{shortDescription}</p>
          )}
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <PriceTag cents={basePrice} compareAtCents={compareAtPrice ?? undefined} className="text-base" />
            {!!soldCount && <span className="text-xs text-ink-muted">{soldCount} sold</span>}
          </div>
          {!!rating && rating.count > 0 && (
            <div className="flex items-center gap-1 mt-1.5">
              <div className="flex text-accent">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={12} fill={i < Math.round(rating.average) ? "currentColor" : "none"} />
                ))}
              </div>
              <span className="text-xs text-ink-muted">({rating.count})</span>
            </div>
          )}
        </div>
      </Link>
    </Card>
  );
}
